import { ClientMsgKind, type Player, PlayerStatus, Role } from '@infiltre/shared';
import { useState } from 'react';
import { selectRoom } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { selectCurrentRevealPlayer } from '../model/selectors';
import { DossierReveal } from '../ui/DossierReveal';
import { PassDeviceCard } from '../ui/PassDeviceCard';

/**
 * Per-player stage state lives in `RevealStage` keyed by player id so it
 * resets cleanly between players (pass → reveal → pass next).
 */
export const WordRevealOrchestrator = () => {
  const player = useGameStore(selectCurrentRevealPlayer);
  const room = useGameStore(selectRoom);
  const handoffIndex = useGameStore((s) =>
    s.handoff.kind === 'word-reveal' ? s.handoff.index : -1,
  );

  if (!player || !room) return null;

  return (
    <RevealStage
      key={player.id}
      player={player}
      step={handoffIndex + 1}
      total={room.players.length}
      isLast={handoffIndex >= room.players.length - 1}
    />
  );
};

const RevealStage = ({
  player,
  step,
  total,
  isLast,
}: {
  player: Player;
  step: number;
  total: number;
  isLast: boolean;
}) => {
  const playSound = useSound();
  const advanceWordReveal = useGameStore((s) => s.advanceWordReveal);
  const resetHandoff = useGameStore((s) => s.resetHandoff);
  const driver = useSessionStore((s) => s.driver);
  const room = useGameStore(selectRoom);
  const [stage, setStage] = useState<'pass' | 'envelope'>('pass');

  if (!driver || !room) return null;

  if (stage === 'pass') {
    return (
      <PassDeviceCard
        playerName={player.name}
        step={step}
        total={total}
        fileNumber={(player.order ?? 0) + 1}
        onReady={() => setStage('envelope')}
      />
    );
  }

  return (
    <DossierReveal
      playerName={player.name}
      playerOrder={player.order}
      playerId={player.id}
      playerMugshot={mugshotForPlayer(player)}
      role={player.role ?? Role.Flic}
      word={player.word ?? ''}
      step={step}
      total={total}
      onReveal={() => playSound(SoundCue.WordReveal)}
      onAcknowledge={() => {
        playSound(SoundCue.AcknowledgeWord);
        if (isLast) {
          // Engine gates Reveal → Discuss on unanimous ack — single device
          // synthesizes one AcknowledgeWord per alive player.
          for (const alive of room.players.filter((p) => p.status === PlayerStatus.Alive)) {
            driver.send({ kind: ClientMsgKind.AcknowledgeWord, actorPlayerId: alive.id });
          }
          resetHandoff();
          return;
        }
        advanceWordReveal();
      }}
    />
  );
};
